import React, { useMemo, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

/**
 * 数値入力用の TextField ラッパ（先頭0バグ対策・予約STGフィードバック第3弾 Wave A）。
 *
 * 課題: `<TextField type="number" value={numberState} />` に数値を直接束ねると、
 * 初期値 0 が「0」と表示され、そこへ続けて入力すると「01000」のように先頭 0 が残る。
 * また value を常に数値へ丸めると、入力途中の空文字を保持できずカーソルが暴れる。
 *
 * 対策: 内部で表示用の文字列バッファを持ち、
 * - 束ねられた数値と一致する間はバッファを使わず数値をそのまま整形して表示（外部更新に追従）、
 * - ユーザー入力中はバッファを優先し、先頭0除去・空許容を行う、
 * - 変更は常に「正規化した数値」で親へ通知（空文字は emptyValue、既定は 0）。
 *
 * これにより 0 は空表示され、先頭 0 が積み上がらない。数値以外の props は TextField へ委譲する。
 */
export interface NumberFieldProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'type'> {
  /** 現在の数値。null/undefined は空表示にする（emptyValue と併用） */
  value: number | null | undefined;
  /** 正規化後の数値を通知する。空入力時は emptyValue（既定 0）を渡す */
  onChangeValue: (value: number) => void;
  /** 空入力時に親へ渡す値（既定 0）。null 許容フィールドは onChangeValue 側で扱う */
  emptyValue?: number;
  /** 小数を許可する（既定は整数のみ・切り捨て） */
  allowDecimal?: boolean;
  /** 下限（指定時は通知値をクランプ） */
  min?: number;
  /** 上限（指定時は通知値をクランプ） */
  max?: number;
}

/** 表示文字列 → 正規化した数値（不正・空は null） */
const parseNumeric = (raw: string, allowDecimal: boolean): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return null;
  return allowDecimal ? n : Math.floor(n);
};

/** 先頭の余分な 0 を落とす（"01000"→"1000"、"0"→"0"、"0.5"/"00.5"→"0.5"） */
const stripLeadingZeros = (raw: string): string => {
  // 符号は数値入力では扱わない前提（min>=0 想定）だが安全側で保持する
  const negative = raw.startsWith('-');
  let s = negative ? raw.slice(1) : raw;
  // 小数点前の整数部だけ先頭0を除去する
  const dot = s.indexOf('.');
  const intPart = dot === -1 ? s : s.slice(0, dot);
  const rest = dot === -1 ? '' : s.slice(dot);
  const strippedInt = intPart.replace(/^0+(?=\d)/, '');
  s = (strippedInt === '' ? '0' : strippedInt) + rest;
  return negative ? `-${s}` : s;
};

const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChangeValue,
  emptyValue = 0,
  allowDecimal = false,
  min,
  max,
  onFocus,
  onBlur,
  inputProps,
  ...rest
}) => {
  // 入力途中の表示文字列。null=バッファ未使用（束ねられた数値をそのまま表示）
  const [buffer, setBuffer] = useState<string | null>(null);

  // 束ねられた数値の表示（0 は空表示にして先頭0の積み上がりを防ぐ）
  const boundText = useMemo(() => {
    if (value == null) return '';
    if (value === 0) return '';
    return String(value);
  }, [value]);

  // バッファがあり、その正規化値が現在の value と一致する間はバッファを表示に使う
  // （一致しなくなった＝外部で値が変わったらバッファを捨てて追従する）
  const displayValue = useMemo(() => {
    if (buffer === null) return boundText;
    const parsed = parseNumeric(buffer, allowDecimal);
    const normalized = parsed == null ? emptyValue : parsed;
    return normalized === (value ?? emptyValue) ? buffer : boundText;
  }, [buffer, boundText, value, emptyValue, allowDecimal]);

  const clamp = (n: number): number => {
    let v = n;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stripped = stripLeadingZeros(e.target.value);
    setBuffer(stripped);
    const parsed = parseNumeric(stripped, allowDecimal);
    onChangeValue(clamp(parsed == null ? emptyValue : parsed));
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={displayValue}
      onChange={handleChange}
      onFocus={(e) => {
        // フォーカス時は現在の表示（0=空）からバッファ編集を開始する
        setBuffer(displayValue);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        // 離脱時はバッファを破棄し、束ねられた数値の整形表示へ戻す
        setBuffer(null);
        onBlur?.(e);
      }}
      inputProps={{ inputMode: allowDecimal ? 'decimal' : 'numeric', min, max, ...inputProps }}
    />
  );
};

export default NumberField;
